import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Environment, EnvironmentService } from '@galaxy/core';
import { TranslateService } from '@ngx-translate/core';
import {
  Category,
  ConnectedProperty,
  DeletePropertyRequest,
  GA4PropertiesApiService,
  GetAvailablePropertiesRequest,
  SetPropertyMultiRequest,
} from '@vendasta/google-analytics';
import { QuickBooksService } from '@vendasta/quickbooks';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  expand,
  map,
  Observable,
  of,
  reduce,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { AccountGroupService } from '../account-group';
import { AuthService } from '../auth.service';
import { GA4View, GAView, SocialPage } from './interface';
import {
  ConnectionsService,
  GetConnectionsResponseInterface,
  GetConnectionsInfoResponseInterface,
  ConnectionInterface,
} from '@vendasta/business-center';

const googleSearchConsoleURL = {
  availableSitesV2: '/ajax/v2/google-search-console/get-available-sites/',
  connectSiteV2: '/ajax/v2/google-search-console/connect-site/',
};

export interface SocialConnectionLinks {
  facebook: string;
  googleplus: string;
  linkedin: string;
  twitter: string;
  googleAnalytics: string;
  googleAnalyticsGA4: string;
  zoom: string;
  google_meet: string;
  quickbooks: string;
  googleSearchConsole: string;
}

interface AccountMeta {
  accounts: SocialPage[];
  cursor: string;
}

export interface SocialPageMeta {
  clientTags: string[];
  deleteUrl: string;
  isVerified: boolean;
  name: string;
  pages: SocialPageMeta[];
  ssid: string;
  tokenIsBroken: boolean;
  type: string;
  locationId: string;
  mapsUri: string;
}

export interface SocialConnections {
  facebook: SocialPageMeta[];
  google_my_business: SocialPageMeta[];
  google_analytics: SocialPageMeta[];
  google_analyticsGA4: SocialPageMeta[];
  zoom: SocialPageMeta[];
  google_meet: SocialPageMeta[];
  quickbooks: SocialPageMeta[];
  google_search_console: SocialPageMeta[];
}

export interface SocialConnectionsInfo {
  facebook: boolean;
  instagram: boolean;
  linkedin: boolean;
  twitter: boolean;
}
type pageResponse = any[];

@Injectable({ providedIn: 'root' })
export class SocialConnectionsService {
  pagesForAccount: Map<string, AccountMeta> = new Map();

  private pages$$ = new BehaviorSubject<SocialPage[]>([]);
  readonly pages$ = this.pages$$.asObservable();
  private loadingPages$$ = new BehaviorSubject<boolean>(false);
  readonly loadingPages$ = this.loadingPages$$.asObservable();
  private pagesCursor$$ = new BehaviorSubject<string>('');
  readonly pagesCursor$ = this.pagesCursor$$.asObservable();
  private accountsCursor$$ = new BehaviorSubject<string>('');
  readonly accountsCursor$ = this.accountsCursor$$.asObservable();
  private accounts$$ = new BehaviorSubject<SocialPage[]>([]);
  readonly accounts$ = this.accounts$$.asObservable();
  private loadingAccounts$$ = new BehaviorSubject<boolean>(false);
  readonly loadingAccounts$ = this.loadingAccounts$$.asObservable();
  private views$$: BehaviorSubject<GAView[]> = new BehaviorSubject<GAView[]>([]);
  readonly views$: Observable<GAView[]> = this.views$$.asObservable();
  private GA4views$$: BehaviorSubject<GA4View[]> = new BehaviorSubject<GA4View[]>([]);
  readonly GA4views$: Observable<GA4View[]> = this.GA4views$$.asObservable();

  private sites$$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  readonly sites$: Observable<string[]> = this.sites$$.asObservable();

  socialProfileConnections$ = this.accountGroupService.currentAccountGroup$.pipe(
    switchMap((accountGroup) => this.getSocialProfileConnections(accountGroup.accountGroupId)),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  socialProfileConnectionsInfo$ = this.accountGroupService.currentAccountGroup$.pipe(
    switchMap((accountGroup) => this.getSocialProfileConnectionsInfo(accountGroup.accountGroupId)),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  gmbConnected$ = this.socialProfileConnections$.pipe(
    map((connections) => connections?.google_my_business?.length > 0),
  );

  googleAnalyticsConnectedGA4$ = this.socialProfileConnections$.pipe(
    map((connections) => connections?.google_analyticsGA4?.length > 0),
  );

  facebookConnected$ = this.socialProfileConnections$.pipe(
    map((connections) => {
      return !!connections.facebook?.some((connection) => connection?.pages?.length > 0);
    }),
  );

  quickbooksConnectedCompany$ = this.accountGroupService.currentAccountGroupId$.pipe(
    switchMap((agid) => this.quickBooksService.getConnectedCompany(agid, this.authService.userId())),
    catchError((err) => {
      if (err.status >= 500) {
        console.error(this.translateService.instant('SOCIAL_CONNECTIONS.QUICKBOOKS.LOADING_ERROR'));
      }
      return of(null);
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  noGaViews$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  noGaViews$ = this.noGaViews$$.asObservable();

  constructor(
    private http: HttpClient,
    private accountGroupService: AccountGroupService,
    private connectionsService: ConnectionsService,
    private readonly environment: EnvironmentService,
    private quickBooksService: QuickBooksService,
    private translateService: TranslateService,
    private ga: GA4PropertiesApiService,
    private authService: AuthService,
  ) {}

  getAuthUrls(accountGroupId: string, nextUrl: Location): Observable<SocialConnectionLinks> {
    let newUrl = nextUrl.href;
    const urlParts = nextUrl.pathname.split('/');
    urlParts.forEach((part) => {
      if (part.startsWith('AG-')) {
        newUrl = newUrl.replace(part, accountGroupId);
      }
    });
    return this.http
      .get<{ data: SocialConnectionLinks }>('/ajax/v1/get-auth-urls/', {
        params: {
          accountGroupId: accountGroupId,
          nextUrl: newUrl,
        },
      })
      .pipe(
        map((response) => {
          return response.data;
        }),
      );
  }

  getSocialProfileConnections(accountGroupId: string): Observable<SocialConnections> {
    return this.connectionsService.getConnections(accountGroupId).pipe(
      map((r: GetConnectionsResponseInterface) => getConnectionsResponseInterfaceToSocialConnections(r)),
      catchError(() => of({} as SocialConnections)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  getSocialProfileConnectionsInfo(accountGroupId: string): Observable<SocialConnectionsInfo> {
    return this.connectionsService.getConnectionsInfo(accountGroupId).pipe(
      map((r: GetConnectionsInfoResponseInterface) => {
        return {
          facebook: r.connectionInfo?.facebook,
          instagram: r.connectionInfo?.instagram,
          linkedin: r.connectionInfo?.linkedin,
          twitter: r.connectionInfo?.twitter,
        };
      }),
      catchError(() => of({} as SocialConnectionsInfo)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  getSocialAccountInformation(
    accountGroupId: string,
    serviceUserId: string,
    type: string,
    cursor?: string,
    accountId?: string,
  ): void {
    this.loadingAccounts$$.next(true);
    this.getSocialPageInformation(accountGroupId, serviceUserId, type, cursor, accountId).subscribe((response) => {
      this.accountsCursor$$.next(response.cursor || '');
      this.loadingAccounts$$.next(false);
      this.accounts$$.next([...this.accounts$$.getValue(), ...response.pages]);
    });
  }

  getSocialPageInformation(
    accountGroupId: string,
    serviceUserId: string,
    type: string,
    cursor?: string,
    accountId?: string,
  ): Observable<SocialPageResponse> {
    return this.http
      .get<VAPIResponse>('/ajax/v1/get-social-page-info/', {
        params: {
          accountGroupId,
          serviceUserId,
          type,
          accountId,
          nextCursor: cursor,
        },
      })
      .pipe(
        map(
          (res): SocialPageResponse => ({
            cursor: res.data.cursor,
            pages: res.data.pages,
          }),
        ),
      );
  }

  getAllSocialPageInformation(
    accountGroupId: string,
    serviceUserId: string,
    type: string,
    cursor?: string,
    accountId?: string,
  ): Observable<pageResponse> {
    return this.getSocialPageInformation(accountGroupId, serviceUserId, type, cursor, accountId).pipe(
      expand((response) => {
        if (!response.cursor) {
          return EMPTY;
        } else {
          return this.getSocialPageInformation(accountGroupId, serviceUserId, type, response.cursor, accountId);
        }
      }),
      reduce((all, { pages }) => all.concat(pages), []),
    );
  }

  connectSocialPage(accountGroupId: string, serviceUserId: string, type: string, pageId: string): Observable<any> {
    return this.http
      .post<any>(
        '/ajax/v1/connect-social-page/',
        {},
        {
          params: {
            accountGroupId: accountGroupId,
            serviceUserId: serviceUserId,
            type: type,
            pageId: pageId,
          },
        },
      )
      .pipe(map((response) => response));
  }

  deleteService(deleteUrl: string): Observable<VAPIResponse> {
    return this.http.post<VAPIResponse>(deleteUrl, {});
  }
  // Google Analytics GA4 property deletion
  deleteGA4Service(partnerId: string, accountGroupId: string, _propertyId: string): Observable<any> {
    return this.ga.deleteProperty(
      new DeletePropertyRequest({
        partnerId: partnerId,
        businessId: accountGroupId,
        category: Category.CATEGORY_WEBSITE,
        subcategory: 'CATEGORY_WEBSITE',
      }),
    );
  }
  // Google Analytics
  buildGoogleAnalyticsAuthUrl(partnerId: string, accountGroupId: string): string {
    // determine if the first query parameter needs an & or an ?
    const queryParamCharacter = window.location.search ? '&' : '?';
    // encode the URI
    const returnUrl = encodeURIComponent(
      window.location.href + queryParamCharacter + 'type=google-analytics' + `&message=VBC`,
    );
    const uri = `${this.googleAnalyticsHost()}/oauth/v1/auth-user`;
    const uriEncodedParams = `partner_id=${partnerId}&business_id=${accountGroupId}&return_url=${returnUrl}`;
    return `${uri}?${uriEncodedParams}`;
  }

  buildGoogleAnalyticsGA4AuthUrl(partnerId: string, accountGroupId: string): string {
    // determine if the first query parameter needs an & or an ?
    const queryParamCharacter = window.location.search ? '&' : '?';
    // encode the URI
    const returnUrl = encodeURIComponent(
      window.location.href + queryParamCharacter + 'type=google-analyticsGA4' + `&message=VBC`,
    );
    const uri = `${this.googleAnalyticsHost()}/oauth/v1/auth-user`;
    const uriEncodedParams = `partner_id=${partnerId}&business_id=${accountGroupId}&return_url=${returnUrl}`;
    return `${uri}?${uriEncodedParams}`;
  }
  private googleAnalyticsHost(): string {
    switch (this.environment.getEnvironment()) {
      case Environment.LOCAL:
        return 'http://localhost:11001';
      case Environment.DEMO:
        return 'https://google-analytics-api-demo.apigateway.co';
      case Environment.PROD:
        return 'https://google-analytics-api-prod.apigateway.co';
    }
    return 'https://google-analytics-api-demo.apigateway.co';
  }

  getPropertiesInformation(googleUserID: string): void {
    this.loadingAccounts$$.next(true);
    this.ga
      .getAvailableProperties(new GetAvailablePropertiesRequest({ googleUserId: googleUserID }))
      .pipe(
        tap((response) => {
          this.GA4views$$.next(response.properties ?? []);
          this.loadingAccounts$$.next(false);
        }),
      )
      .subscribe();
  }

  connectGA4Prop(
    partnerId: string,
    accountGroupId: string,
    accountId: string,
    propertyId: string,
    gaUserId: string,
    category: number,
    subcategory: string,
    propertyName: string,
    accountName: string,
  ): any {
    const param: ConnectedProperty[] = [
      new ConnectedProperty({
        partnerId: partnerId,
        businessId: accountGroupId,
        category: Category.CATEGORY_WEBSITE,
        subcategory: subcategory,
        propertyId: propertyId,
        propertyName: propertyName,
        accountId: accountId,
        accountName: accountName,
        googleUserId: gaUserId,
      }),
    ];
    this.ga
      .setPropertyMulti(
        new SetPropertyMultiRequest({
          properties: param,
        }),
      )
      .subscribe();
  }
  // Google Search Console
  buildGoogleSearchConsoleAuthUrl(partnerId: string, accountGroupId: string): string {
    // determine if the first query parameter needs an & or an ?
    const queryParamCharacter = window.location.search ? '&' : '?';
    // encode the URI
    let newUrl = window.location.href;
    const urlParts = window.location.pathname.split('/');
    urlParts.forEach((part) => {
      if (part.startsWith('AG-')) {
        newUrl = newUrl.replace(part, accountGroupId);
      }
    });
    const returnUrl = encodeURIComponent(newUrl + queryParamCharacter + 'type=google-search-console' + `&message=VBC`);
    const uri = `${this.googleSearchConsoleHost()}/oauth/v1/auth-user`;
    const uriEncodedParams = `partner_id=${partnerId}&business_id=${accountGroupId}&return_url=${returnUrl}`;
    return `${uri}?${uriEncodedParams}`;
  }

  private googleSearchConsoleHost(): string {
    switch (this.environment.getEnvironment()) {
      case Environment.LOCAL:
        return 'http://localhost:11001';
      case Environment.DEMO:
        return 'https://google-search-console-demo.apigateway.co';
      case Environment.PROD:
        return 'https://google-search-console-prod.apigateway.co';
    }
    return 'https://google-search-console-demo.apigateway.co';
  }

  gscGetSitesInformation(googleUserID: string): void {
    this.loadingAccounts$$.next(true);
    this.http
      .get<VAPIResponse>(googleSearchConsoleURL.availableSitesV2, {
        params: {
          googleUserId: googleUserID,
        },
      })
      .pipe(
        tap((response) => {
          this.sites$$.next(response.data);
          this.loadingAccounts$$.next(false);
        }),
      )
      .subscribe();
  }

  gscConnectSite(
    partnerId: string,
    accountGroupId: string,
    googleUserId: string,
    gscSiteUrl: string,
  ): Observable<VAPIResponse> {
    const params = {
      partnerId: partnerId,
      businessId: accountGroupId,
      gscSiteUrl: gscSiteUrl,
      googleUserId: googleUserId,
    };
    return this.http
      .post<VAPIResponse>(
        googleSearchConsoleURL.connectSiteV2,
        {},
        {
          params: params,
        },
      )
      .pipe(map((response) => response.data));
  }
}

function getConnectionsResponseInterfaceToSocialConnections(r: GetConnectionsResponseInterface): SocialConnections {
  if (!r.connections) {
    return {} as SocialConnections;
  }
  return {
    facebook: r.connections['facebook']?.connection?.map((page: ConnectionInterface) =>
      connectionToSocialPageMeta(page),
    ),
    google_my_business: r.connections['google_my_business']?.connection?.map((page: ConnectionInterface) =>
      connectionToSocialPageMeta(page),
    ),
    google_analytics: r.connections['google_analytics']?.connection?.map((page: ConnectionInterface) =>
      connectionToSocialPageMeta(page),
    ),
    google_analyticsGA4: r.connections['google_analyticsGA4']?.connection?.map((page: ConnectionInterface) =>
      connectionToSocialPageMeta(page),
    ),
    zoom: r.connections['zoom']?.connection?.map((page: ConnectionInterface) => connectionToSocialPageMeta(page)),
    google_meet: r.connections['google_meet']?.connection?.map((page: ConnectionInterface) =>
      connectionToSocialPageMeta(page),
    ),
    quickbooks: r.connections['quickbooks']?.connection?.map((page: ConnectionInterface) =>
      connectionToSocialPageMeta(page),
    ),
    google_search_console: r.connections['google_search_console']?.connection?.map((page: ConnectionInterface) =>
      connectionToSocialPageMeta(page),
    ),
  };
}

function connectionToSocialPageMeta(connection: ConnectionInterface): SocialPageMeta {
  if (!connection) {
    return null;
  }
  return {
    clientTags: connection.clientTags,
    deleteUrl: connection.deleteUrl,
    isVerified: connection.isVerified,
    name: connection.name,
    pages: connection.pages?.map((page: SocialPageMeta) => connectionToSocialPageMeta(page)),
    ssid: connection.ssid,
    tokenIsBroken: connection.tokenIsBroken,
    type: connection.type,
    locationId: connection.locationId,
    mapsUri: connection.mapsUri,
  };
}

export interface VAPIResponse {
  data: any;
  requestId: string;
  responseTime: number;
  statusCode: number;
  version: string;
}
export interface SocialPageResponse {
  cursor?: string;
  pages: any[];
}
